import { Component, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { LoggerService } from '../../shared/logger/logger.service';
import { CommonModule } from '@angular/common';
import { ApiService } from '../../api/api.service';
import { AuthTokenService } from '../../auth/auth-token.service';
import { AuthService } from '../../auth/auth.service';
import { FontAwesomeModule, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faCoffee, faPoo, faLock, faEnvelope, faSpinner, } from '@fortawesome/free-solid-svg-icons';
import { captchaExpiryMinutes, CasiCloseOptions, EMAIL_REGEX, MessageStates, PHONE_REGEX } from '../../common/constants.lib';
import { invalidMobileValidator, invalidPhoneValidator } from '../../directives/casi.validators';
import { UserEnquiry } from '../../common/obj.lib';
import { EnquiresService } from '../enquires.service';
import { BehaviorSubject } from 'rxjs';
import { CaptchaComponent } from '../../custom-accessors/captcha/captcha.component';
import { TrackerApiService } from '../../api/tracker-api.service';
import { DialogCloseResult, DialogService, DialogsModule } from '@progress/kendo-angular-dialog';
import { CaptchaRequiredComponent } from '../../custom-accessors/captcha-required/captcha-required.component';
import { ErrorDialogModalComponent } from '../../common-components/error-dialog-modal/error-dialog-modal.component';
import { CaptchaServiceService } from '../../shared/captcha-service.service';

@Component({
  selector: 'app-contact-us',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule, ReactiveFormsModule,
    FontAwesomeModule,
    DialogsModule,
    ErrorDialogModalComponent,
    CaptchaRequiredComponent
  ],
  templateUrl: './contact-us.component.html',
  styleUrl: './contact-us.component.scss'
})
export class ContactUsComponent implements OnInit {

  @ViewChild("appendTo", { read: ViewContainerRef }) appendTo: ViewContainerRef | undefined;

  contactForm: FormGroup;
  MessageStates = MessageStates;

  emailRegx: string = EMAIL_REGEX;
  phonePattern: string = PHONE_REGEX
  showCaptcha = false;

  focusCounter: number = 0;

  private _messageState = new BehaviorSubject<MessageStates>(MessageStates.Before);
  get messageState() {
    return this._messageState.getValue();
  }
  set messageState(val: MessageStates) {
    this._messageState.next(val);
  }

  constructor(private logger: LoggerService,
    public authTokenService: AuthTokenService,
    private fb: FormBuilder,
    public faIcons: FaIconLibrary,
    private dataService: EnquiresService,
    private tracker: TrackerApiService,
    private captchaService: CaptchaServiceService,
    private dialogService: DialogService,
  ) {
    faIcons.addIcons(faEnvelope);
    this.contactForm = this.fb.group({
      id: [null, []],
      firstName: [null, []],
      lastName: [null, []],
      phone: [null, [invalidPhoneValidator]],
      email: [null, [Validators.required, Validators.email]],
      message: [null, [Validators.required]],
      // captchaValid: [null, [Validators.required]]
    });
  }

  //#region Form properties

  get f() {
    return this.contactForm.controls;
  }
  get email() {
    return this.contactForm.get('email');
  }
  get firstName() {
    return this.contactForm.get('firstName');
  }
  get lastName() {
    return this.contactForm.get('lastName');
  }
  get phone() {
    return this.contactForm.get('phone');
  }
  get message() {
    return this.contactForm.get('message');
  }
  get captchaValid() {
    return this.contactForm.get('captchaValid');
  }

  //#endregion

  //#region Ng handlers

  ngOnInit(): void {
    this.setForm();
    this.tracker.logActivity('Contact Us Page', this);
  }

  //#endregion

  //#region Public methods

  setForm() {
    this.messageState = MessageStates.Before; // 'before';

    if (this.authTokenService.loggedIn) {
      let userInfo = this.authTokenService.getClaims();
      this.logger.debug('userInfo', userInfo);
      this.contactForm.patchValue({ ...userInfo });
    } else {
      this.contactForm.patchValue({});
    }

    this.removeCaptchaValid();
    this.showCaptcha = false;
  }

  sendClicked() {
    this.logger.debug(`sendClicked...`);
    this.addCaptchaValid();
    this.showCaptcha = true;
    this.resetCaptchaText();
  }

  cancelEnquiry() {
    this.logger.debug('cancelEnquiry...');
    // $state.go('base');
  }

  resetForm() {
    this.logger.debug('resetForm...');
    this.contactForm.reset();
    this.setForm();
  }

  onSubmit() {
    this.logger.debug('onSubmit...', this.contactForm.value);
    const { captchaValid } = this.contactForm.value;
    let toSubmit = UserEnquiry.create(this.contactForm.value);
    this.logger.debug('onSubmit...', toSubmit.forSubmit());
    return this.verifyBeforeSubmit(captchaValid)
      .then(resp => {
        this.logger.debug(`vm.submitEnquiry:resp: `, resp);
        if (resp.iamhuman === true) {
          this.submitEnquiry(toSubmit);
        } else {
          this.logger.error(`vm.submitEnquiry: Not valid!`);
          this.resetCaptchaText();
        }
      });
    // this.dataService.createEnquiry(toSubmit.forSubmit())
    //     .then(data => {
    //       this.messageState = MessageStates.After;
    //     })
    //     .catch(err => {
    //       this.logger.error('onSubmit:err: ', err.message);
    //       this.messageState = MessageStates.Error; // 'error'
    //       this.logger.debug('onSubmit:messageState:', this.messageState);
    //     })
  }

  captchaHandler(evt: any) {
    this.logger.debug(`captchaHandler: evt: `, evt);
  }

  //#endregion

  //#region Private methods

  private addCaptchaValid() {
    this.handleFormCaptchaValid();
  }
  private removeCaptchaValid() {
    this.handleFormCaptchaValid(false);
  }
  private handleFormCaptchaValid(flag: boolean = true) {
    if (flag) {
      this.contactForm.addControl('captchaValid', new FormControl('', Validators.required));
    } else {
      this.contactForm.removeControl('captchaValid');
    }
  }

  private submitEnquiry(toSubmit: UserEnquiry) {
    this.logger.debug('_submitEnquiry...', toSubmit.forSubmit());
    this.messageState = MessageStates.InProgress; // "inprogress";

    this.dataService.createEnquiry(toSubmit.forSubmit())
      .then(data => {
        this.messageState = MessageStates.After;
      })
      .catch(err => {
        this.logger.error('onSubmit:err: ', err.message);
        this.messageState = MessageStates.Error; // 'error'
        this.logger.debug('onSubmit:messageState:', this.messageState);
      })
  }

  private resetCaptchaText() {
    this.contactForm.patchValue({
      captchaValid: ''
    })
  }
  private verifyBeforeSubmit(captchaText: string = ''): Promise<any> {
    return this.captchaService
      .verifyCaptchaInputTextOnly(captchaText.toUpperCase(), true)
      .then(resp => {
        this.logger.debug(`verifyBeforeSubmit:resp: `, resp);
        if (!resp.iamhuman === true) {
          this.openErrorDailog("INCORRECT OR EXPIRED CAPTCHA",
            `This CAPTHA is either incorrect or expired. CAPTCHAs expire after ${captchaExpiryMinutes} minutes
          for security purposes. Please recapture a new value.`,
            "OK");
        }
        return resp;
      })
      .catch(err => {
        this.logger.error(`verifyBeforeSubmit:error: `, err.message);
        return Promise.reject(err);
      })
  }

  private openErrorDailog(title: string = 'Not set', content: string = 'No content', buttonText: string = 'Close'): void {
    this.logger.debug(`openErrorDialog: `, {
      title,
      content,
      buttonText
    });
    const dialogRef = this.dialogService.open({
      content: ErrorDialogModalComponent,
      minWidth: 320,
      width: 750,
      appendTo: this.appendTo,
    });

    const contentRef = dialogRef.content.instance as ErrorDialogModalComponent;
    contentRef.title = title;
    contentRef.content = content;
    contentRef.buttonText = buttonText;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openErrorDailog:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Closed) {
        this.logger.debug("openErrorDailog:ok to proceed... ");
        this.focusCounter++;
      }
    });

  }

  //#endregion

}
